/**
 * Body of the persistent warning toast shown when a forward-week cascade failed after its
 * primary write had already landed (see toastCascadeFailed in ./toast). Presentational: the
 * caller's onRetry resolves true once the cascade lands, and is responsible for dismissing.
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { colors as theme } from '../theme';

const colors = {
  warning: theme.warning,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
  dark: theme.bg,
};

const buttonBase = {
  border: 'none',
  borderRadius: 8,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const CascadeFailedToast = ({ visible, message, onRetry, onDismiss }) => {
  const [busy, setBusy] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const handleRetry = async () => {
    if (busy) return;
    setBusy(true);
    setRetryFailed(false);
    let landed = false;
    try {
      landed = await onRetry();
    } catch {
      landed = false;
    }
    // On success the caller dismisses this toast, so only the failure path touches state.
    if (!landed) {
      setBusy(false);
      setRetryFailed(true);
    }
  };

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        maxWidth: 440,
        background: colors.bgCard,
        color: colors.text,
        border: `1px solid ${colors.warning}`,
        borderRadius: 8,
        padding: '12px 16px',
        fontSize: 14,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 200ms',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <AlertTriangle size={18} color={colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div>{message}</div>
        {retryFailed && (
          <div style={{ color: colors.textMuted, marginTop: 4 }}>
            Still couldn't reach the later weeks. Check the connection and try again.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            onClick={handleRetry}
            disabled={busy}
            style={{
              ...buttonBase,
              background: colors.warning,
              color: colors.dark,
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Retrying…' : 'Retry'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            style={{
              ...buttonBase,
              background: 'transparent',
              color: colors.textMuted,
              border: `1px solid ${colors.border}`,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default CascadeFailedToast;
